import React, { useEffect, useState } from "react";
import { ReactQuestionFactory } from "survey-react-ui";
import { Question, Serializer, ElementFactory } from "survey-core";

export class YearPickerQuestionModel extends Question {
  static readonly type = "yearpicker";

  constructor(name: string) {
    super(name);
  }

  getType() {
    return YearPickerQuestionModel.type;
  }

  public getDefaultValue(): unknown {
    return undefined;
  }

  public isValueEmpty(value: unknown): boolean {
    return value === undefined || value === null || value === "";
  }
}

interface YearPickerQuestionComponentProps {
  question: YearPickerQuestionModel;
}

const VALUE_CHANGE_KEY = "yearpicker-value";

const extractYear = (raw: unknown): string | null => {
  if (typeof raw !== "string" || raw.length === 0) return null;
  const match = /^(\d{4})/.exec(raw);
  return match ? match[1] : null;
};

export function YearPickerQuestionComponent({
  question,
}: YearPickerQuestionComponentProps) {
  const [value, setValue] = useState<string | null>(() =>
    extractYear(question.value),
  );

  useEffect(() => {
    const handler = () => {
      setValue(extractYear(question.value));
    };
    question.registerFunctionOnPropertyValueChanged(
      "value",
      handler,
      VALUE_CHANGE_KEY,
    );
    return () => {
      question.unRegisterFunctionOnPropertyValueChanged(
        "value",
        VALUE_CHANGE_KEY,
      );
    };
  }, [question]);

  const isReadOnly = question.isReadOnly;
  const currentYear = new Date().getFullYear();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (!next) {
      setValue(null);
      question.clearValue();
      return;
    }
    const year = extractYear(next);
    if (!year) {
      return;
    }
    setValue(year);
    question.value = year;
  };

  return (
    <div className={question.cssClasses?.root}>
      <input
        type="number"
        value={value ?? ""}
        onChange={handleChange}
        disabled={isReadOnly}
        min={1900}
        max={currentYear}
        placeholder="YYYY"
      />
    </div>
  );
}

export function registerYearPickerQuestion() {
  ElementFactory.Instance.registerElement(
    "yearpicker",
    (name: string) => new YearPickerQuestionModel(name),
  );

  Serializer.addClass(
    "yearpicker",
    [],
    () => new YearPickerQuestionModel(""),
    "question",
  );

  ReactQuestionFactory.Instance.registerQuestion(
    YearPickerQuestionModel.type,
    (propsOrName: string | YearPickerQuestionComponentProps) => {
      const props =
        typeof propsOrName === "string"
          ? { question: new YearPickerQuestionModel(propsOrName) }
          : propsOrName;
      return React.createElement(YearPickerQuestionComponent, props);
    },
  );
}
